import { format, subDays } from 'date-fns';

import {
  DailyServiceMetrics,
  EnvironmentHealthStatus,
  PublicDailyServiceMetrics,
  PublicEnvironmentHealthStatus,
  ServiceStatus,
} from '@/types/service/serviceHealth';

/**
 * Get Tailwind color classes for a given service status
 */
export const getStatusColor = (status: ServiceStatus): { bg: string; text: string } => {
  switch (status) {
    case ServiceStatus.OPERATIONAL:
      return { bg: 'bg-emerald-500', text: 'text-emerald-700' };
    case ServiceStatus.DEGRADED:
      return { bg: 'bg-amber-500', text: 'text-amber-700' };
    case ServiceStatus.DOWN:
      return { bg: 'bg-red-500', text: 'text-red-700' };
    default:
      return { bg: 'bg-gray-200', text: 'text-gray-700' };
  }
};

/**
 * Generate array of last 90 days (YYYY-MM-DD format)
 */
export const generateLast90Days = (): string[] => {
  const days: string[] = [];
  const today = new Date();

  for (let i = 89; i >= 0; i--) {
    const date = subDays(today, i);
    days.push(format(date, 'yyyy-MM-dd'));
  }

  return days;
};

/**
 * Fill missing days in metrics with "no data" placeholders (Public API version)
 */
export const fillMissingDays = (
  metrics: PublicDailyServiceMetrics[],
): Array<PublicDailyServiceMetrics | null> => {
  const last90Days = generateLast90Days();
  const metricsMap = new Map(metrics.map((m) => [m.date, m]));

  return last90Days.map((date) => metricsMap.get(date) || null);
};

/**
 * Fill missing days in metrics with "no data" placeholders (Protected API version)
 */
export const fillMissingDaysProtected = (
  metrics: DailyServiceMetrics[],
): Array<DailyServiceMetrics | null> => {
  const last90Days = generateLast90Days();
  const metricsMap = new Map(metrics.map((m) => [m.date, m]));

  return last90Days.map((date) => metricsMap.get(date) || null);
};

/**
 * Calculate status for a single environment (Public API version)
 */
export const calculateEnvironmentStatus = (
  environment: PublicEnvironmentHealthStatus,
): { status: 'operational' | 'degraded' | 'down'; message: string } => {
  let hasDown = false;
  let hasDegraded = false;

  for (const service of environment.services) {
    if (service.current_status === ServiceStatus.DOWN) {
      hasDown = true;
    } else if (service.current_status === ServiceStatus.DEGRADED) {
      hasDegraded = true;
    }
  }

  if (hasDown) {
    return { status: 'down', message: 'Some services are currently down' };
  }

  if (hasDegraded) {
    return { status: 'degraded', message: 'Some services are experiencing issues' };
  }

  return { status: 'operational', message: 'All systems operational' };
};

/**
 * Calculate status for a single environment (Protected API version)
 */
export const calculateEnvironmentStatusProtected = (
  environment: EnvironmentHealthStatus,
): { status: 'operational' | 'degraded' | 'down'; message: string } => {
  let hasDown = false;
  let hasDegraded = false;

  for (const service of environment.services) {
    if (service.current_status === ServiceStatus.DOWN) {
      hasDown = true;
    } else if (service.current_status === ServiceStatus.DEGRADED) {
      hasDegraded = true;
    }
  }

  if (hasDown) {
    return { status: 'down', message: 'Some services are currently down' };
  }

  if (hasDegraded) {
    return { status: 'degraded', message: 'Some services are experiencing issues' };
  }

  return { status: 'operational', message: 'All systems operational' };
};

/**
 * Calculate overall status for all environments (Public API version)
 */
export function calculateOverallStatus(environments: PublicEnvironmentHealthStatus[]): {
  status: 'operational' | 'degraded' | 'down';
  message: string;
};
/**
 * Calculate overall status for all environments (Protected API version)
 */
export function calculateOverallStatus(environments: EnvironmentHealthStatus[]): {
  status: 'operational' | 'degraded' | 'down';
  message: string;
};
export function calculateOverallStatus(
  environments: PublicEnvironmentHealthStatus[] | EnvironmentHealthStatus[],
): { status: 'operational' | 'degraded' | 'down'; message: string } {
  let hasDown = false;
  let hasDegraded = false;

  for (const env of environments) {
    for (const service of env.services) {
      if (service.current_status === ServiceStatus.DOWN) {
        hasDown = true;
      } else if (service.current_status === ServiceStatus.DEGRADED) {
        hasDegraded = true;
      }
    }
  }

  if (hasDown) {
    return { status: 'down', message: 'Some services are currently down' };
  }

  if (hasDegraded) {
    return { status: 'degraded', message: 'Some services are experiencing issues' };
  }

  return { status: 'operational', message: 'All systems operational' };
}

/**
 * Get human-readable service name from ServiceType enum
 */
export const getServiceDisplayName = (serviceType: string): string => {
  const nameMap: Record<string, string> = {
    fundfit: 'FundFit',
    reporting: 'Reporting',
    research: 'Research',
    sso: 'Authentication',
  };

  return nameMap[serviceType] || serviceType;
};

/**
 * Format incident type for display
 */
export const getIncidentTypeDisplay = (type: string): { label: string; color: string } => {
  switch (type) {
    case 'maintenance':
      return { label: 'Maintenance', color: 'text-blue-600' };
    case 'event':
      return { label: 'Event', color: 'text-orange-600' };
    default:
      return { label: type, color: 'text-gray-600' };
  }
};

/**
 * Sort environments to show production first (Public API version)
 */
export const sortEnvironments = (
  environments: PublicEnvironmentHealthStatus[],
): PublicEnvironmentHealthStatus[] => {
  return [...environments].sort((a, b) => {
    if (a.environment_type === 'production' && b.environment_type !== 'production') {
      return -1;
    }
    if (a.environment_type !== 'production' && b.environment_type === 'production') {
      return 1;
    }
    return a.environment.localeCompare(b.environment);
  });
};

/**
 * Sort environments to show production first (Protected API version)
 */
export const sortEnvironmentsProtected = (
  environments: EnvironmentHealthStatus[],
): EnvironmentHealthStatus[] => {
  return [...environments].sort((a, b) => {
    if (a.environment_type === 'production' && b.environment_type !== 'production') {
      return -1;
    }
    if (a.environment_type !== 'production' && b.environment_type === 'production') {
      return 1;
    }
    return a.environment.localeCompare(b.environment);
  });
};

/**
 * Format uptime percentage for display (for protected DevOps API)
 */
export const formatUptime = (uptime: number): string => {
  return uptime.toFixed(2);
};
